import {HttpClient, httpResource} from '@angular/common/http';
import {Injectable, OnDestroy, Signal} from '@angular/core';
import {Store} from '@ngrx/store';
import {UserService} from 'auth-module/services/user.service';

import {fromEvent, merge, Observable, of, Subject} from 'rxjs';
import {map, startWith, switchMap, take} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {getShowUnsearchable} from '../../state-management/reducers';
import {HistoryEvent, HistoryEventBlock} from '../model/history-events';
import {startPolling} from '../utils/longPollWithRetry';
import {DateTranslateService} from './date-translate.service';
import {toSignal} from "@angular/core/rxjs-interop";

export interface HistoryLoaderResponse {
  result: HistoryEventBlock[];
  hasMoreAfter: boolean | null;
  hasMoreBefore: boolean | null;
}

export interface HistoryLoaderV2Response {
  result: HistoryEventBlock[];
  total: number;
  pageSize: number;
  links: {
    previous?: string;
    next?: string;
    self?: string;
  }
}

@Injectable({
  providedIn: 'root'
})
export class HistoryLoaderService implements OnDestroy {
  private static readonly POLL_INTERVAL = 60_000;

  private stopPolling$ = new Subject<void>();
  private forceReload$ = new Subject<void>()
  private user$ = this.userService.user$;
  private showUnsearchable$ = this.store.select(getShowUnsearchable);
  private showUnsearchableSignal = toSignal(this.showUnsearchable$);

  private userLoggedOut$ = this.user$.pipe(map(user => !user));
  private historyResults$: Observable<HistoryLoaderResponse> = this.user$.pipe(
    switchMap((user) => {
    if (!!user) {
      return startPolling(() => this.loadHistory(), {
        requestInterval: HistoryLoaderService.POLL_INTERVAL,
        forceStop: this.stopPolling$,
        forceReload: this.forceReload$,
        pause: merge(this.userLoggedOut$, fromEvent(document, 'visibilitychange').pipe(map(() => document.hidden), startWith(document.hidden)))
      });
    } else {
      return of(null);
    }
  }))

  constructor(private http: HttpClient, private userService: UserService,
    private dateTranslateService: DateTranslateService, private store: Store) {
  }

  get eventBlocks(): Observable<HistoryEventBlock[]> {
    return this.historyResults$.pipe(map(response => response?.result || null));
  }

  convertEntry = (entry: HistoryEventBlock): HistoryEventBlock => {
    return {
      commitId: entry.commitId,
      user: entry.user,
      datetime: this.dateTranslateService.transform(new Date(entry.datetime)),
      events: entry.events as HistoryEvent[]
    }
  }

  forceReload(): void {
    this.forceReload$.next();
  }

  ngOnDestroy(): void {
    this.stopPolling$.next();
  }

  public loadHistory(all: boolean = false, cursorId: number = 0,
    previous: boolean = false): Observable<HistoryLoaderResponse> {
    return this.showUnsearchable$.pipe(
      take(1),
      switchMap((showUnsearchable) => {
        return this.http.get<HistoryLoaderResponse>(environment.api + this.userService.domain + '/history/', {
          params: {
            all,
            cursorId,
            previous,
            skipUnsearchable: !showUnsearchable
          }
        }).pipe(map((response: any) => {
          const convertedMap: any[] = response.result.map(this.convertEntry);
          if (!all) {
            convertedMap.reverse()
          }
          return {
            result: convertedMap,
            hasMoreBefore: response.hasMoreBefore ?? null,
            hasMoreAfter: response.hasMoreAfter ?? null
          };
        }));
      }));
  }

  public paginatedHistoryResource(urlSignal: Signal<string|null>) {
    return httpResource<HistoryLoaderV2Response>(() => {
      let url = urlSignal();
      if (!url) {
        url = `${environment.api}${this.userService.domain}/history/v2?skipUnsearchable=${this.showUnsearchableSignal() ? 'false' : 'true'}`
      }

      return {url, reportProgress: true};
    }, {parse: (response: HistoryLoaderV2Response): HistoryLoaderV2Response => {
        return {...response, result: response.result.filter(block => block.events.length > 0).map(this.convertEntry)};
      }});
  }
}
